/**
 * EvaluationGradebook.jsx — Phase 2C C6: Gradebook Handoff + Publication
 *
 * Acceptance rules enforced:
 *   1. Shows RATIFIED cases with resolved marks — awaiting Phase 1E handoff.
 *   2. Provides "Trigger Handoff" button per ratification_record (idempotent).
 *   3. Shows outbox events queued for Phase 1E publication pipeline.
 *   4. Clearly distinguishes RATIFIED ≠ PUBLISHED.
 *   5. PUBLISHED cases shown separately — student visibility unblocked only then.
 *   6. Coordinator + system roles only.
 *   7. Handoff is idempotent — safe to re-trigger.
 *   8. Architecture boundary: RATIFIED → Phase 1E handoff → Phase 1E conditions → PUBLISHED.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Lock,
  BookOpen,
  Zap,
  Info,
  Eye,
  EyeOff,
  Send,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { EvaluationShell } from "../components/evaluation/EvaluationShell";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const OFFERING_ID = "2c000040-2c00-2c00-2c00-2c0000000040";
const COMPONENT_ID = "2c000014-2c00-2c00-2c00-2c0000000014";

function authH() {
  return { Authorization: `Bearer ${localStorage.getItem("academiaos_token")}` };
}
async function apiFetch(path, opts = {}) {
  const r = await fetch(`${API}${path}`, {
    headers: { ...authH(), "Content-Type": "application/json" },
    ...opts,
  });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(body?.detail?.message || String(body?.detail || r.statusText)), { status: r.status });
  return body;
}

// ─── Status pill ──────────────────────────────────────────────────────────────

function StatusPill({ status }) {
  const styles = {
    RATIFIED:   "bg-amber-950 text-amber-300 border-amber-800/60",
    PUBLISHED:  "bg-green-950 text-green-300 border-green-800/60",
    OUTBOX_QUEUED: "bg-sky-950 text-sky-300 border-sky-800/60",
    WRITTEN:    "bg-teal-950 text-teal-300 border-teal-800/60",
    ERROR:      "bg-red-950 text-red-300 border-red-800/60",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${styles[status] || "bg-slate-800 text-slate-400 border-slate-700"}`}>
      {status?.replace(/_/g, " ")}
    </span>
  );
}

// ─── Pipeline step ────────────────────────────────────────────────────────────

function PipelineStep({ step, done, active, note }) {
  return (
    <div className={`flex items-start gap-3 px-4 py-3 rounded-lg border ${
      done   ? "border-teal-800/40 bg-teal-950/10" :
      active ? "border-amber-800/40 bg-amber-950/10" :
               "border-slate-800/40 bg-slate-900/20"
    }`}>
      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 border ${
        done   ? "border-teal-700 bg-teal-900/50" :
        active ? "border-amber-700 bg-amber-900/50" :
                 "border-slate-700 bg-slate-800/50"
      }`}>
        {done ? (
          <CheckCircle2 className="w-3.5 h-3.5 text-teal-400" />
        ) : active ? (
          <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
        ) : (
          <div className="w-2 h-2 rounded-full bg-slate-600" />
        )}
      </div>
      <div>
        <div className={`text-xs font-semibold ${done ? "text-teal-300" : active ? "text-amber-300" : "text-slate-500"}`}>
          {step}
        </div>
        {note && <div className="text-[10px] text-slate-600 mt-0.5">{note}</div>}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function EvaluationGradebook() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [handoffBusy, setHandoffBusy] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(
        `/evaluation/gradebook-status?offering_id=${OFFERING_ID}&component_id=${COMPONENT_ID}`
      );
      setData(res);
    } catch (e) {
      toast.error("Failed to load gradebook status: " + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const triggerHandoff = async (recordId) => {
    setHandoffBusy((prev) => ({ ...prev, [recordId]: true }));
    try {
      const res = await apiFetch("/evaluation/gradebook-handoff", {
        method: "POST",
        body: JSON.stringify({ ratification_record_id: recordId }),
      });
      const written = res.results?.length ?? res.written?.length ?? 0;
      const mode = res.handoff_mode || (res.results?.[0]?.status === "OUTBOX_QUEUED" ? "OUTBOX_QUEUED" : "WRITTEN");
      if (mode === "OUTBOX_QUEUED") {
        toast.success(`Handoff queued for Phase 1E pipeline. ${written} events emitted. Student visibility gated on Phase 1E publication.`);
      } else {
        toast.success(`Gradebook handoff complete. ${written} record(s) written to Phase 1E.`);
      }
      await load();
    } catch (e) {
      toast.error("Handoff failed: " + e.message);
    } finally {
      setHandoffBusy((prev) => ({ ...prev, [recordId]: false }));
    }
  };

  const ratifiedCases    = data?.ratified_cases    ?? [];
  const publishedCases   = data?.published_cases   ?? [];
  const records          = data?.ratification_records ?? [];
  const outboxEvents     = data?.outbox_events      ?? [];

  const allRatified  = ratifiedCases.length > 0;
  const allPublished = publishedCases.length > 0 && ratifiedCases.length === 0;

  return (
    <EvaluationShell title="Gradebook" breadcrumb="Gradebook">
      <div className="space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-20 gap-3 text-slate-500">
            <RefreshCw className="w-5 h-5 animate-spin" />
            <span>Loading gradebook status…</span>
          </div>
        ) : (
          <>
            {/* ── Publication Pipeline ── */}
            <div className="rounded-xl border border-slate-800 overflow-hidden" data-testid="publication-pipeline">
              <div className="flex items-center gap-2 px-5 py-3.5 bg-slate-900/60 border-b border-slate-800">
                <BookOpen className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-semibold text-slate-300">Publication Pipeline</span>
                <span className="text-xs text-slate-600 ml-2">
                  RATIFIED ≠ PUBLISHED — student visibility blocked until Phase 1E conditions are met
                </span>
              </div>
              <div className="p-5 grid grid-cols-1 sm:grid-cols-4 gap-3">
                <PipelineStep
                  step="Phase 2C Ratified"
                  done={allRatified || allPublished}
                  active={!allRatified && !allPublished}
                  note={`${data?.total_ratified ?? 0} case(s) ratified`}
                />
                <PipelineStep
                  step="Gradebook Handoff"
                  done={outboxEvents.length > 0}
                  active={allRatified && outboxEvents.length === 0}
                  note={outboxEvents.length > 0 ? `${outboxEvents.length} event(s) queued` : "Pending trigger"}
                />
                <PipelineStep
                  step="Phase 1E Conditions"
                  done={allPublished}
                  active={outboxEvents.length > 0 && !allPublished}
                  note="Awaiting Phase 1E publication"
                />
                <PipelineStep
                  step="Published"
                  done={allPublished}
                  active={false}
                  note={allPublished ? `${data?.total_published ?? 0} case(s) visible to students` : "Blocked"}
                />
              </div>
            </div>

            {/* ── Handoff trigger ── */}
            {records.length > 0 && (
              <div className="rounded-xl border border-slate-800 overflow-hidden" data-testid="handoff-trigger-section">
                <div className="flex items-center gap-2 px-5 py-3.5 bg-slate-900/60 border-b border-slate-800">
                  <Zap className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-semibold text-slate-300">Gradebook Handoff</span>
                </div>
                <div className="p-5 space-y-3">
                  {records.map((rec) => (
                    <div
                      key={rec.id}
                      data-testid={`handoff-record-${rec.id}`}
                      className="flex items-center gap-4 p-4 rounded-xl border border-slate-800 bg-slate-900/40"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <StatusPill status={rec.status} />
                          <span className="text-xs text-slate-500 font-mono">{rec.id?.slice(0, 16)}…</span>
                        </div>
                        <div className="text-[10px] text-slate-600 mt-0.5">
                          Ratified at: {rec.ratified_at ? new Date(rec.ratified_at).toLocaleString() : "—"}
                          {rec.notes && <span className="ml-2 italic">"{rec.notes}"</span>}
                        </div>
                      </div>
                      <button
                        data-testid={`trigger-handoff-${rec.id}`}
                        onClick={() => triggerHandoff(rec.id)}
                        disabled={handoffBusy[rec.id]}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-sky-700/40 hover:bg-sky-700/60 border border-sky-700/40 text-sky-300 text-xs font-medium transition-colors disabled:opacity-50 whitespace-nowrap"
                      >
                        {handoffBusy[rec.id] ? (
                          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Send className="w-3.5 h-3.5" />
                        )}
                        Trigger Handoff (Idempotent)
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── RATIFIED cases ── */}
            {ratifiedCases.length > 0 && (
              <div data-testid="ratified-cases-section">
                <div className="flex items-center gap-2 mb-3">
                  <EyeOff className="w-4 h-4 text-amber-400" />
                  <span className="text-sm font-semibold text-amber-300">
                    RATIFIED — Awaiting Phase 1E Handoff ({ratifiedCases.length})
                  </span>
                  <span className="text-xs text-slate-600">Student visibility: blocked</span>
                </div>
                <div className="rounded-xl border border-slate-800 overflow-hidden">
                  <div className="grid grid-cols-[1fr_80px_80px_120px] gap-3 px-4 py-2 bg-slate-800/50 text-[10px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-700/40">
                    <span>Case</span><span className="text-center">Mark</span><span className="text-center">Max</span><span>Source</span>
                  </div>
                  {ratifiedCases.map((c) => (
                    <div key={c.id} data-testid={`ratified-case-${c.id}`}
                      className="grid grid-cols-[1fr_80px_80px_120px] gap-3 px-4 py-3 border-b border-slate-800/50 last:border-b-0 text-xs">
                      <span className="text-slate-500 font-mono">{c.id?.slice(0, 16)}…</span>
                      <span className="text-center font-mono font-bold text-amber-300">{c.resolved_mark}</span>
                      <span className="text-center text-slate-500">{c.max_mark}</span>
                      <span className="text-slate-600">{c.resolved_source?.replace(/_/g, " ")}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── PUBLISHED cases ── */}
            {publishedCases.length > 0 && (
              <div data-testid="published-cases-section">
                <div className="flex items-center gap-2 mb-3">
                  <Eye className="w-4 h-4 text-green-400" />
                  <span className="text-sm font-semibold text-green-300">
                    PUBLISHED — Visible to Students ({publishedCases.length})
                  </span>
                </div>
                <div className="rounded-xl border border-teal-800/40 overflow-hidden">
                  <div className="grid grid-cols-[1fr_80px_80px_120px] gap-3 px-4 py-2 bg-teal-900/20 text-[10px] font-semibold text-teal-600 uppercase tracking-wider border-b border-teal-800/30">
                    <span>Case</span><span className="text-center">Mark</span><span className="text-center">Max</span><span>Source</span>
                  </div>
                  {publishedCases.map((c) => (
                    <div key={c.id} data-testid={`published-case-${c.id}`}
                      className="grid grid-cols-[1fr_80px_80px_120px] gap-3 px-4 py-3 border-b border-teal-900/20 last:border-b-0 text-xs">
                      <span className="text-slate-400 font-mono">{c.id?.slice(0, 16)}…</span>
                      <span className="text-center font-mono font-bold text-green-300">{c.resolved_mark}</span>
                      <span className="text-center text-slate-500">{c.max_mark}</span>
                      <span className="text-slate-500">{c.resolved_source?.replace(/_/g, " ")}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Outbox events ── */}
            {outboxEvents.length > 0 && (
              <div data-testid="outbox-events-section">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                  Phase 1E Handoff Events ({outboxEvents.length})
                </div>
                <div className="space-y-1">
                  {outboxEvents.slice(0, 5).map((ev) => (
                    <div key={ev.id} data-testid={`outbox-event-${ev.id}`}
                      className="flex items-center gap-3 px-4 py-2 rounded-lg bg-sky-950/20 border border-sky-800/30 text-xs">
                      <CheckCircle2 className="w-3.5 h-3.5 text-sky-400 flex-shrink-0" />
                      <span className="text-sky-300 font-mono">{ev.event_type}</span>
                      <span className="text-slate-600">{new Date(ev.created_at).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty state */}
            {!loading && ratifiedCases.length === 0 && publishedCases.length === 0 && (
              <div data-testid="empty-gradebook" className="flex flex-col items-center py-16 text-slate-500">
                <BookOpen className="w-10 h-10 mb-3 opacity-30" />
                <div className="text-sm font-medium">No ratified cases yet</div>
                <div className="text-xs mt-1 text-slate-600">
                  Cases appear here after ratification is complete.
                </div>
              </div>
            )}

            {/* Architecture notice */}
            <div className="flex items-start gap-2 text-[10px] text-slate-600 bg-slate-900/40 border border-slate-800/60 rounded-lg px-4 py-3">
              <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>
                <strong className="text-slate-500">Architecture boundary:</strong>{" "}
                Completing the gradebook handoff does not immediately make results visible to students.
                The sequence is: RATIFIED → Phase 1E handoff succeeds → Phase 1E publication conditions satisfied → PUBLISHED.
                Student visibility is blocked until all Phase 1E conditions are met and the case status reaches PUBLISHED.
              </span>
            </div>

            {/* Refresh */}
            <div className="flex justify-end">
              <button data-testid="refresh-gradebook-btn" onClick={load} disabled={loading}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-700 text-slate-400 text-xs hover:border-slate-600 hover:text-slate-200 disabled:opacity-40 transition-colors">
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>
            </div>
          </>
        )}
      </div>
    </EvaluationShell>
  );
}
